import React, { useEffect, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import Image from '~/components/Common/Image';

import type { MediaImage } from '../ImagesVideo';

import { ROOM_IMAGE_SLOT_COUNT } from './utils';
import type { RoomImage } from './utils';

interface Props {
  mediaImages: Array<MediaImage>;
  open: boolean;
  roomImages: Array<RoomImage>;
  roomName: string;
  onAddImages: (selectedImages: Array<MediaImage>) => void;
  onClose: () => void;
}

export default function ImagesByRoomPickerDialog({
  mediaImages,
  open,
  roomImages,
  roomName,
  onAddImages,
  onClose,
}: Props) {
  const existingPublicIds = useMemo(() => new Set(roomImages.map((image) => image.publicId)), [roomImages]);
  const slotsRemaining = useMemo(() => Math.max(ROOM_IMAGE_SLOT_COUNT - roomImages.length, 0), [roomImages.length]);
  const [selectedPublicIds, setSelectedPublicIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setSelectedPublicIds(new Set());
    }
  }, [open]);

  useEffect(() => {
    setSelectedPublicIds((currentSelectedPublicIds) => {
      const nextSelectedPublicIds = Array.from(currentSelectedPublicIds)
        .filter((publicId) => !existingPublicIds.has(publicId))
        .slice(0, slotsRemaining);

      if (
        nextSelectedPublicIds.length === currentSelectedPublicIds.size &&
        nextSelectedPublicIds.every((publicId) => currentSelectedPublicIds.has(publicId))
      ) {
        return currentSelectedPublicIds;
      }

      return new Set(nextSelectedPublicIds);
    });
  }, [existingPublicIds, slotsRemaining]);

  const handleToggleImage = (image: MediaImage) => {
    if (existingPublicIds.has(image.publicId)) {
      return;
    }

    setSelectedPublicIds((currentSelectedPublicIds) => {
      const nextSelectedPublicIds = new Set(currentSelectedPublicIds);

      if (nextSelectedPublicIds.has(image.publicId)) {
        nextSelectedPublicIds.delete(image.publicId);
        return nextSelectedPublicIds;
      }

      if (nextSelectedPublicIds.size >= slotsRemaining) {
        return currentSelectedPublicIds;
      }

      nextSelectedPublicIds.add(image.publicId);
      return nextSelectedPublicIds;
    });
  };

  const handleAddToRoom = () => {
    onAddImages(mediaImages.filter((image) => selectedPublicIds.has(image.publicId)));
    onClose();
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
      <DialogTitle>
        <Stack spacing={1}>
          <Typography variant="h4">Add images to {roomName}</Typography>
          <Typography variant="body1" color="text.secondary">
            {slotsRemaining} {slotsRemaining === 1 ? 'slot' : 'slots'} remaining. Select images from the library to add
            to this room.
          </Typography>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Typography variant="body1" color="text.secondary">
            Select images to add
          </Typography>
          <Button
            variant="text"
            onClick={() => setSelectedPublicIds(new Set())}
            disabled={selectedPublicIds.size === 0}
          >
            Clear selection
          </Button>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 2,
            maxHeight: '60vh',
            overflowY: 'auto',
            pr: 1,
          }}
        >
          {mediaImages.map((image) => {
            const isExistingImage = existingPublicIds.has(image.publicId);
            const isSelected = selectedPublicIds.has(image.publicId);

            return (
              <Button
                key={image.publicId}
                variant="text"
                disabled={isExistingImage}
                onClick={() => handleToggleImage(image)}
                sx={imagePickerTileButtonSx}
              >
                <Box sx={imagePickerTileSx}>
                  <Image
                    publicId={image.publicId}
                    alt={image.title || image.sourceLabel}
                    options={{ width: 360, height: 270 }}
                    draggable={false}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  <Chip label={image.sourceLabel} size="small" sx={imagePickerSourceChipSx} />
                  <Box
                    sx={{
                      ...imagePickerSelectionSx,
                      borderColor: isSelected ? 'primary.main' : 'grey.300',
                      bgcolor: isSelected ? 'primary.main' : 'common.white',
                    }}
                  />
                  {isExistingImage && <Box sx={imagePickerExistingOverlaySx} />}
                </Box>
              </Button>
            );
          })}
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          disabled={selectedPublicIds.size === 0}
          onClick={handleAddToRoom}
        >
          Add to room
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const imagePickerTileButtonSx = {
  minWidth: 0,
  p: 0,
  borderRadius: 1,
  textTransform: 'none',
  '&.Mui-disabled': {
    opacity: 0.52,
  },
};

const imagePickerTileSx = {
  position: 'relative',
  overflow: 'hidden',
  aspectRatio: '4 / 3',
  width: '100%',
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  bgcolor: 'background.default',
};

const imagePickerSourceChipSx = {
  position: 'absolute',
  top: 10,
  left: 10,
  maxWidth: 'calc(100% - 62px)',
  bgcolor: 'grey.800',
  color: 'common.white',
  fontWeight: 700,
  '& .MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

const imagePickerSelectionSx = {
  position: 'absolute',
  top: 10,
  right: 10,
  width: 34,
  height: 34,
  border: 3,
  borderRadius: '50%',
  boxShadow: 2,
};

const imagePickerExistingOverlaySx = {
  position: 'absolute',
  inset: 0,
  bgcolor: 'rgba(255, 255, 255, 0.3)',
};
